import { SheetWithFormComponent } from '@/components/ui/SheetWithFormComponent'
import { useRestClient } from '@/contexts/QueryProvider'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { FormComponentLoading } from '@/catalog-apps/components/component-library/FormComponent'
import { Button } from '@/components/shadcn/button'
import { Form, FormField, FormItem, FormMessage } from '@/components/shadcn/form'
import { useCurrentOrganisation } from '../useCurrentOrganisation'
import { ErrorComponent } from '@/ui-legacy/ErrorComponent'
import { ApplicationRoleAssignmentsField } from './components/ApplicationRoleAssignmentsField'
import { UserOrNomineeListField } from './components/RequestFormFields'
import {
  useWorkspaceProjectApplicationOptions,
  WorkspaceProjectApplicationFields,
} from './components/WorkspaceProjectApplicationFields'
import { applicationAssignmentSchema, userOrNomineeSchema } from '../Requests/schemas'
import {
  ApplicationAssignment,
  isCompletePerson,
  REQUEST_TICKET_TYPE_WORKSPACE_CREATE,
  UserOrNominee,
  WorkspaceCreateTicket,
} from '../Requests/types'

const formSchema = z.object({
  projectId: z.number().min(1, 'Project is required'),
  applicationId: z.number().min(1, 'Application is required'),
  people: z
    .array(userOrNomineeSchema)
    .min(1, 'At least one person is required')
    .refine((people) => people.every(isCompletePerson), {
      message: 'Each person must be a selected user or a complete nominee',
    }),
  assignments: z
    .array(applicationAssignmentSchema)
    .min(1, 'At least one person is required')
    .refine((assignments) => assignments.every((assignment) => assignment.roleIds.length > 0), {
      message: 'Assign at least one role to each person',
    }),
})

type FormValues = z.infer<typeof formSchema>

type RequestWorkspaceFormProps = {
  onSubmit?: () => void
  sheetTrigger?: React.ReactNode
  sheetTriggerTooltip?: string
  sheetTitle?: string
  sheetDescription?: string
  organisationId?: number
  projectId: number
  projectName?: string
  excludedAppIds?: number[]
}

export function RequestWorkspaceForm(props: RequestWorkspaceFormProps) {
  const [sheetOpen, setSheetOpen] = useState(false)

  const { onSubmit: onSubmitProp, ...rest } = props
  const onSubmit = useCallback(() => {
    onSubmitProp?.()
    setSheetOpen(false)
  }, [onSubmitProp])

  return (
    <SheetWithFormComponent
      title={props.sheetTitle}
      description={props.sheetDescription}
      trigger={props.sheetTrigger}
      triggerTooltip={props.sheetTriggerTooltip}
      wide
      isOpen={sheetOpen}
      onOpenChange={setSheetOpen}
    >
      <FormComponent {...rest} onSubmit={onSubmit} />
    </SheetWithFormComponent>
  )
}

function FormComponent({
  onSubmit,
  organisationId,
  projectId,
  excludedAppIds = [],
}: RequestWorkspaceFormProps & { onSubmit?: () => void }) {
  const { id: orgId } = useCurrentOrganisation(organisationId)

  const {
    getSubscriptionsApiListOrganisationUsers,
    putRequestsApiAddRequest,
    getRequestsApiGetOrganisationRequests,
    getProjectsApiGetUserLinkedProjects,
  } = useRestClient()

  const { data: users } = getSubscriptionsApiListOrganisationUsers.useQuery(
    { query: { orgId: orgId! } },
    { enabled: !!orgId }
  )

  const defaultValues = useMemo(
    () => ({
      projectId,
      applicationId: 0,
      people: [] as UserOrNominee[],
      assignments: [] as ApplicationAssignment[],
    }),
    [projectId]
  )

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues,
  })

  useEffect(() => {
    form.setValue('projectId', projectId)
  }, [form, projectId])

  const selectedAppId = form.watch('applicationId')
  const people = (form.watch('people') ?? []) as UserOrNominee[]
  const assignments = (form.watch('assignments') ?? []) as ApplicationAssignment[]

  const { loading, error } = useWorkspaceProjectApplicationOptions(orgId, projectId, excludedAppIds)

  const userOptions = useMemo(
    () =>
      users?.map((user) => ({
        value: user.id!,
        label: `${user.givenName} ${user.familyName}`,
        description: user.email,
      })) ?? [],
    [users]
  )

  const { mutate, isPending } = putRequestsApiAddRequest.useMutation(undefined, {
    onSuccess: () => {
      getRequestsApiGetOrganisationRequests.invalidateQueries({ parameters: { query: { orgId } } })
      getProjectsApiGetUserLinkedProjects.invalidateQueries({})
      toast.success('Workspace create request submitted')
      onSubmit?.()
      form.reset({ ...defaultValues })
    },
    onError: () => toast.error('Failed to submit workspace create request'),
  })

  const handleSubmit = form.handleSubmit(
    (values) => {
      if (!orgId) {
        toast.error('No organisation ID found')
        return
      }

      const ticket: WorkspaceCreateTicket = {
        projectId: values.projectId,
        applicationId: values.applicationId,
        people: values.assignments as ApplicationAssignment[],
      }

      mutate({
        body: {
          ticketType: REQUEST_TICKET_TYPE_WORKSPACE_CREATE,
          organisationId: orgId,
          ticket: JSON.stringify(ticket),
        },
      })
    },
    () => {
      toast.error('Please select an application and give every person at least one role')
    }
  )

  if (loading) return <FormComponentLoading />
  if (error) return <ErrorComponent />

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit}>
        <div className="flex h-full flex-col">
          <div className="space-y-6">
            <WorkspaceProjectApplicationFields
              control={form.control}
              orgId={orgId}
              lockedProjectId={projectId}
              excludedAppIds={excludedAppIds}
              showProjectPicker={false}
            />
            <FormField
              control={form.control}
              name="people"
              render={() => (
                <FormItem>
                  <UserOrNomineeListField
                    label="Workspace people"
                    required
                    userOptions={userOptions}
                    value={people}
                    onChange={(next) => form.setValue('people', next, { shouldValidate: true })}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="assignments"
              render={() => (
                <FormItem>
                  <ApplicationRoleAssignmentsField
                    label="Application roles"
                    applicationId={selectedAppId}
                    people={people}
                    userOptions={userOptions}
                    value={assignments}
                    onChange={(next) => form.setValue('assignments', next, { shouldValidate: true })}
                    noPeopleMessage="Select or nominate people above to assign their application roles."
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="form-action-container">
            <Button type="submit" disabled={isPending}>
              Request Workspace
            </Button>
          </div>
        </div>
      </form>
    </Form>
  )
}
